import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { useAuth0 } from '@auth0/auth0-react';
import {
  GetCampaignGrowthActionsResponse,
  CampaignErrorResponse,
  AgentType,
  CampaignEvents,
} from '@dreamai/types';
import api from '../../../api';
import CampaignActionsModal from './CampaignActionsModal';
import { useAgent } from '../../../contexts/AgentContext';
import { usePusher } from '../../../contexts/PusherContext';

import { ReactComponent as LinkedInIcon } from '../../../icons/linkedin-blue.svg';
import { ReactComponent as InstagramIcon } from '../../../icons/instagram.svg';

import './campaign-actions.scss';

interface CampaignActionsProps {
  campaignId: number;
  threadId?: string;
}

type ModalMode = 'dual' | 'linkedin' | 'instagram' | null;

const CampaignActions: React.FC<CampaignActionsProps> = ({
  campaignId,
  threadId,
}) => {
  const { getAccessTokenSilently } = useAuth0();
  const { agents } = useAgent();
  const pusherChannel = usePusher();
  const [loading, setLoading] = useState(true);
  const [growthData, setGrowthData] =
    useState<GetCampaignGrowthActionsResponse | null>(null);
  const [modalMode, setModalMode] = useState<ModalMode>(null);

  // Get Sales Manager nickname
  const salesManagerNickname = useMemo(() => {
    const salesManager = agents.find(
      (agent) => (agent.agent_type as string) === 'Sales Assistant',
    );
    return salesManager?.nickname || 'Your Sales Assistant';
  }, [agents]);

  const fetchGrowthActions = useCallback(async () => {
    try {
      setLoading(true);
      const token = await getAccessTokenSilently();
      api.defaults.headers.common['Authorization'] = `Bearer ${token}`;

      const response = await api.get<
        GetCampaignGrowthActionsResponse | CampaignErrorResponse
      >(`/campaigns/${campaignId}/growth-actions`);

      if (response.data.success) {
        setGrowthData(response.data);
      }
    } catch (err) {
      console.error('Failed to fetch growth actions:', err);
    } finally {
      setLoading(false);
    }
  }, [campaignId, getAccessTokenSilently]);

  useEffect(() => {
    if (campaignId) {
      fetchGrowthActions();
    }
  }, [campaignId, fetchGrowthActions]);

  // Listen for Pusher events to reload growth actions
  useEffect(() => {
    if (!pusherChannel) return;

    const handleGrowthActionsUpdate = () => {
      fetchGrowthActions();
    };

    pusherChannel.bind(
      CampaignEvents.GROWTH_ACTIONS_UPDATE,
      handleGrowthActionsUpdate,
    );

    return () => {
      pusherChannel.unbind(
        CampaignEvents.GROWTH_ACTIONS_UPDATE,
        handleGrowthActionsUpdate,
      );
    };
  }, [pusherChannel, fetchGrowthActions]);

  const handleModalClose = () => {
    setModalMode(null);
  };

  const handleSetupComplete = async () => {
    // Refetch growth actions data after setup
    try {
      const token = await getAccessTokenSilently();
      api.defaults.headers.common['Authorization'] = `Bearer ${token}`;

      const response = await api.get<
        GetCampaignGrowthActionsResponse | CampaignErrorResponse
      >(`/campaigns/${campaignId}/growth-actions`);

      if (response.data.success) {
        setGrowthData(response.data);
      }
    } catch (err) {
      console.error('Failed to refetch growth actions:', err);
    }
    setModalMode(null);
  };

  const handleInstagramSetup = async () => {
    if (!threadId) {
      console.error('No thread ID available for Instagram setup');
      return;
    }

    try {
      const token = await getAccessTokenSilently();
      api.defaults.headers.common['Authorization'] = `Bearer ${token}`;

      // Call backend endpoint to trigger Instagram setup via executeThread
      await api.post(`/campaigns/${campaignId}/setup-instagram`, {
        threadId,
      });
    } catch (err) {
      console.error('Failed to trigger Instagram setup:', err);
    }
  };

  // Return null if loading or growth not enabled
  if (loading) {
    return null;
  }

  if (!growthData || !growthData.growthEnabled) {
    return null;
  }

  const { companySettings, campaignSettings, companyName } = growthData;
  const isInstagramSetup = companySettings.instagramDelegated;
  const isInstagramPending = companySettings.instagramVerificationPending;
  const isLinkedInSetup = !!companySettings.linkedinSetup;
  const isLinkedInPending = companySettings.linkedinSetupPending;
  const neitherSetup =
    !isInstagramSetup &&
    !isLinkedInSetup &&
    !isInstagramPending &&
    !isLinkedInPending;

  // Render the "Setup Campaign Actions" view if neither platform is setup
  if (neitherSetup) {
    return (
      <div className='campaign-actions'>
        <div className='campaign-actions-header'>
          <h3 className='campaign-actions-title'>Actions</h3>
        </div>

        <div className='campaign-actions-setup-card'>
          <h4 className='setup-card-title'>Close more with Campaign actions</h4>
          <p className='setup-card-description'>
            Don't wait for interested customers to reply. Setup Campaign Actions
            and {salesManagerNickname} will engage with customers who've opened
            emails, or clicked links. You can review any content before it goes
            out, and even handle things yourself.
          </p>
          <button
            className='setup-campaign-actions-btn'
            onClick={() => setModalMode('dual')}
          >
            Setup Campaign Actions
          </button>
        </div>

        <CampaignActionsModal
          show={modalMode !== null}
          mode={modalMode || 'dual'}
          onClose={handleModalClose}
          onSetupComplete={handleSetupComplete}
          onInstagramSetup={handleInstagramSetup}
          campaignId={campaignId}
          threadId={threadId}
          agentNickname={salesManagerNickname}
          companyName={companyName}
        />
      </div>
    );
  }

  // Render the "Actions pending / Actions taken" view
  const getLinkedInStatus = () => {
    if (isLinkedInPending) {
      return { text: 'Setting up', isLink: false, isPending: true };
    }
    if (!isLinkedInSetup) {
      return {
        text: 'Setup LinkedIn',
        isLink: true,
        onClick: () => setModalMode('linkedin'),
      };
    }
    if (campaignSettings.linkedinGrowthActions) {
      return { text: 'Enabled', isLink: false };
    }
    return { text: 'Not enabled for this campaign', isLink: false };
  };

  const getInstagramStatus = () => {
    if (isInstagramPending) {
      return { text: 'Verifying', isLink: false, isPending: true };
    }
    if (!isInstagramSetup) {
      return {
        text: 'Setup Instagram',
        isLink: true,
        onClick: () => setModalMode('instagram'),
      };
    }
    if (campaignSettings.instagramGrowthActions) {
      return { text: 'Enabled', isLink: false };
    }
    return { text: 'Not enabled for this campaign', isLink: false };
  };

  const linkedInStatus = getLinkedInStatus();
  const instagramStatus = getInstagramStatus();

  return (
    <div className='campaign-actions'>
      <div className='campaign-actions-header'>
        <h3 className='campaign-actions-title'>Actions</h3>
      </div>

      <div className='campaign-actions-content'>
        <div className='campaign-actions-stats'>
          <div className='actions-stat'>
            <span className='actions-stat-label'>Actions pending</span>
            <span className='actions-stat-value'>-</span>
          </div>
          <div className='actions-stat'>
            <span className='actions-stat-label'>Actions taken</span>
            <span className='actions-stat-value'>-</span>
          </div>
        </div>

        <div className='campaign-actions-platforms'>
          {linkedInStatus.isLink ? (
            <button
              className='platform-status platform-setup-btn'
              onClick={linkedInStatus.onClick}
            >
              <div className='platform-icon linkedin-icon'>
                <LinkedInIcon />
              </div>
              <span className='platform-setup-text'>{linkedInStatus.text}</span>
            </button>
          ) : (
            <div
              className={`platform-status ${linkedInStatus.isPending ? 'pending' : ''}`}
            >
              <div className='platform-icon linkedin-icon'>
                <LinkedInIcon />
              </div>
              <span className='platform-status-text'>
                {linkedInStatus.text}
              </span>
              {linkedInStatus.isPending && (
                <div className='platform-status-spinner' />
              )}
            </div>
          )}

          {instagramStatus.isLink ? (
            <button
              className='platform-status platform-setup-btn'
              onClick={instagramStatus.onClick}
            >
              <div className='platform-icon instagram-icon'>
                <InstagramIcon />
              </div>
              <span className='platform-setup-text'>
                {instagramStatus.text}
              </span>
            </button>
          ) : (
            <div
              className={`platform-status ${instagramStatus.isPending ? 'pending' : ''}`}
            >
              <div className='platform-icon instagram-icon'>
                <InstagramIcon />
              </div>
              <span className='platform-status-text'>
                {instagramStatus.text}
              </span>
              {instagramStatus.isPending && (
                <div className='platform-status-spinner' />
              )}
            </div>
          )}
        </div>
      </div>

      <CampaignActionsModal
        show={modalMode !== null}
        mode={modalMode || 'dual'}
        onClose={handleModalClose}
        onSetupComplete={handleSetupComplete}
        onInstagramSetup={handleInstagramSetup}
        campaignId={campaignId}
        threadId={threadId}
        agentNickname={salesManagerNickname}
        companyName={companyName}
      />
    </div>
  );
};

export default CampaignActions;
